import { Terminal } from 'lucide-react';
import clsx from 'clsx';
import { v4 as uuidv4 } from 'uuid';

import { EnvironmentId } from '@/react/portainer/environments/types';
import { useAnalytics } from '@/react/hooks/useAnalytics';
import { baseHref } from '@/portainer/helpers/pathHelper';
import { usePublicSettings } from '@/react/portainer/settings/queries';

import { Button } from '@@/buttons';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';

import { useSidebarState } from '../useSidebarState';
import { SidebarTooltip } from '../SidebarItem/SidebarTooltip';

const FIPS_MODE_MESSAGE = 'kubectl shell is not available in FIPS mode';
const KUBECTL_SHELL_LABEL = 'kubectl shell';

interface Props {
  environmentId: EnvironmentId;
}

export function KubectlShellButton({ environmentId }: Props) {
  const { isOpen: isSidebarOpen } = useSidebarState();
  const { trackEvent } = useAnalytics();

  const { data: publicSettings } = usePublicSettings();
  const isFIPSMode = !!publicSettings?.FIPSMode;

  function handleOpen() {
    const url = window.location.origin + baseHref();
    const windowName = `kubectl-shell-${environmentId}-${uuidv4()}`;

    window.open(
      `${url}#!/${environmentId}/kubernetes/kubectl-shell`,
      windowName,
      'width=800,height=600'
    );

    trackEvent('kubernetes-kubectl-shell', { category: 'kubernetes' });
  }

  const button = (
    <Button
      color="primary"
      size="small"
      data-cy="k8sSidebar-shellButton"
      onClick={handleOpen}
      className={clsx('sidebar', !isSidebarOpen && '!p-1')}
      icon={Terminal}
      disabled={isFIPSMode}
    >
      {isSidebarOpen ? KUBECTL_SHELL_LABEL : ''}
    </Button>
  );

  // Render logic based on sidebar state and FIPS mode
  if (isSidebarOpen) {
    return isFIPSMode ? (
      <TooltipWithChildren message={FIPS_MODE_MESSAGE} position="right">
        {/* div wrapper needed to avoid tippy tooltip error */}
        <div>{button}</div>
      </TooltipWithChildren>
    ) : (
      button
    );
  }

  // Sidebar is closed - wrap button for centering
  const centeredButton = (
    <span className="flex w-full justify-center">{button}</span>
  );

  return isFIPSMode ? (
    <TooltipWithChildren message={FIPS_MODE_MESSAGE} position="right">
      {centeredButton}
    </TooltipWithChildren>
  ) : (
    <SidebarTooltip
      content={
        <span className="whitespace-nowrap text-sm">{KUBECTL_SHELL_LABEL}</span>
      }
    >
      {centeredButton}
    </SidebarTooltip>
  );
}
